/**
 * Lightweight first-party analytics for clicks & conversions.
 *
 * - Attribution lives in sessionStorage so a Facebook visit that later submits
 *   a quote form, taps "call us", or clicks an email link gets credited to FB.
 * - Click events use navigator.sendBeacon when available so the request still
 *   fires when the page navigates away (tel:, mailto:, target=_blank).
 *
 * Server endpoint: POST /api/public/track  (RLS-locked table, server-only writes)
 */

const STORAGE_KEY = "tms.attribution.v1";
const SESSION_KEY = "tms.session.v1";

export type EventName =
  | "facebook_click"
  | "phone_call_click"
  | "sms_click"
  | "email_click"
  | "quote_form_submit"
  | "quote_form_start"
  | "cta_click"
  | "shop_order_submit"
  | "directions_click"
  | "page_view"
  | "search_open"
  | "search_query"
  | "search_suggestion_click"
  | "search_result_click"
  | "search_no_results"
  | "video_embed_blocked"
  | "video_backup_play"
  | "series_badge_click"
  | "series_badge_impression"
  | "series_badge_hover"
  | "series_product_link_click"
  | "series_cta_click";

interface Attribution {
  source: string;       // "facebook" | "google" | "direct" | "referral" | utm_source
  medium: string;       // utm_medium or "social" / "organic" / "referral" / "(none)"
  campaign: string;     // utm_campaign or ""
  referrer: string;     // document.referrer at first hit
  landing_path: string; // first page in this session
  ts: number;           // when this attribution was captured
}

function safeWindow(): Window | null {
  return typeof window === "undefined" ? null : window;
}

function getOrCreateSessionId(): string {
  const w = safeWindow();
  if (!w) return "ssr";
  let id = w.sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id =
      (w.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(
        /-/g,
        "",
      );
    w.sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function detectSourceFromReferrer(ref: string): { source: string; medium: string } {
  if (!ref) return { source: "direct", medium: "(none)" };
  try {
    const host = new URL(ref).host.toLowerCase();
    if (/facebook\.com|fb\.com|m\.facebook\.com|l\.facebook\.com/.test(host)) {
      return { source: "facebook", medium: "social" };
    }
    if (/instagram\.com/.test(host)) return { source: "instagram", medium: "social" };
    if (/google\./.test(host)) return { source: "google", medium: "organic" };
    if (/bing\.com/.test(host)) return { source: "bing", medium: "organic" };
    if (/yelp\.com/.test(host)) return { source: "yelp", medium: "referral" };
    return { source: host, medium: "referral" };
  } catch {
    return { source: "direct", medium: "(none)" };
  }
}

/** Initialise (or refresh) attribution for this session. Call from app root. */
export function initAttribution(): Attribution | null {
  const w = safeWindow();
  if (!w) return null;

  const url = new URL(w.location.href);
  const utmSource = url.searchParams.get("utm_source");
  const utmMedium = url.searchParams.get("utm_medium");
  const utmCampaign = url.searchParams.get("utm_campaign");
  const fbclid = url.searchParams.get("fbclid");

  const existing = w.sessionStorage.getItem(STORAGE_KEY);
  if (existing && !utmSource && !fbclid) {
    try {
      return JSON.parse(existing) as Attribution;
    } catch {
      /* fall through and rewrite */
    }
  }

  const detected = detectSourceFromReferrer(document.referrer || "");
  const attribution: Attribution = {
    source: utmSource || (fbclid ? "facebook" : detected.source),
    medium: utmMedium || (fbclid ? "social" : detected.medium),
    campaign: utmCampaign || (fbclid ? "fbclid" : ""),
    referrer: document.referrer || "",
    landing_path: url.pathname + url.search,
    ts: Date.now(),
  };

  w.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  return attribution;
}

function getAttribution(): Attribution | null {
  const w = safeWindow();
  if (!w) return null;
  const raw = w.sessionStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw) as Attribution;
    } catch {
      /* ignore */
    }
  }
  return initAttribution();
}

interface TrackOptions {
  target?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Map our internal events to GA4 event names.
 *
 * IMPORTANT: only map an event to "generate_lead" / "purchase" if it represents
 * a true conversion. Mapping low-intent clicks (email/sms) to "generate_lead"
 * caused inflated conversion rates in GA4 (≈100% sessions converting).
 *
 * page_view is intentionally omitted — __root.tsx fires page_view directly via
 * gtag on every route change, so mirroring it here would double-count sessions
 * and break GA4 engagement / conversion metrics.
 */
const GA4_EVENT_MAP: Partial<Record<EventName, string>> = {
  phone_call_click: "phone_call_click",

  shop_order_submit: "purchase",
  // Engagement (not conversions):
  sms_click: "sms_click",
  email_click: "email_click",
  directions_click: "directions_click",
  facebook_click: "outbound_click",
  // Signature Collection badge funnel (engagement, not conversions):
  series_badge_click: "series_badge_click",
  series_badge_impression: "series_badge_impression",
  series_badge_hover: "series_badge_hover",
  series_product_link_click: "series_product_link_click",
  series_cta_click: "series_cta_click",
};

// Events we never mirror to GA4 (handled elsewhere or not relevant).
const GA4_SKIP: ReadonlySet<EventName> = new Set<EventName>([
  "page_view", // already sent by __root.tsx on route change
]);

export function ga4EventName(event: EventName, metadata?: Record<string, unknown>): string {
  if (event === "quote_form_submit") return metadata?.status === "success" ? "generate_lead" : "quote_form_attempt";
  return GA4_EVENT_MAP[event] ?? event;
}

function sendToGA4(event: EventName, opts: TrackOptions, page_path: string) {
  const w = safeWindow();
  if (!w) return;
  if (GA4_SKIP.has(event)) return;
  const gtag = (w as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== "function") return;
  const ga4Event = ga4EventName(event, opts.metadata);
  gtag("event", ga4Event, {
    event_category: "engagement",
    event_label: opts.target,
    page_path,
    cta_event: event,
    ...(opts.metadata ?? {}),
  });
}

/** Fire-and-forget event. Uses sendBeacon for reliability on outbound link clicks. */
export function track(event: EventName, opts: TrackOptions = {}) {
  const w = safeWindow();
  if (!w) return;

  const attr = getAttribution();
  const payload = {
    event_name: event,
    session_id: getOrCreateSessionId(),
    source: attr?.source,
    medium: attr?.medium,
    campaign: attr?.campaign,
    referrer: attr?.referrer,
    page_path: w.location.pathname + w.location.search,
    target: opts.target,
    metadata: opts.metadata ?? {},
  };

  // Mirror to GA4 so conversions show up in the Google Analytics dashboard.
  sendToGA4(event, opts, payload.page_path);

  const body = JSON.stringify(payload);
  const url = "/api/public/track";

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
  } catch {
    /* fall through to fetch */
  }

  // Fallback — keepalive lets it survive the unload that follows tel:/mailto:
  void fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
    credentials: "omit",
  }).catch(() => undefined);
}

/* ------------------------------------------------------------------ */
/* Signature Collection badge funnel helpers                          */
/* ------------------------------------------------------------------ */

export interface SeriesTrackContext {
  /** Series slug, e.g. "alamo-series" */
  slug: string;
  /** Human label for reporting, e.g. "Alamo Series" */
  name?: string;
  /** Where the badge/link was rendered: "home_hero", "series_page", "mega_menu", ... */
  placement: string;
}

/** A badge tile itself was clicked. */
export function trackBadgeClick(ctx: SeriesTrackContext, to: string) {
  track("series_badge_click", {
    target: to,
    metadata: { series_slug: ctx.slug, series_name: ctx.name, placement: ctx.placement },
  });
}

/** A product link revealed under a badge was clicked. */
export function trackSeriesProductClick(
  ctx: SeriesTrackContext,
  product: string,
  to: string,
) {
  track("series_product_link_click", {
    target: to,
    metadata: {
      series_slug: ctx.slug,
      series_name: ctx.name,
      placement: ctx.placement,
      product,
    },
  });
}

/** The "explore / view series" CTA under a badge card was clicked. */
export function trackSeriesCta(ctx: SeriesTrackContext, to: string, label?: string) {
  track("series_cta_click", {
    target: to,
    metadata: { series_slug: ctx.slug, series_name: ctx.name, placement: ctx.placement, label },
  });
}

/** First meaningful hover/focus on a badge tile (engagement signal). */
export function trackBadgeHover(ctx: SeriesTrackContext) {
  track("series_badge_hover", {
    target: ctx.slug,
    metadata: { series_slug: ctx.slug, series_name: ctx.name, placement: ctx.placement },
  });
}

/** Badge grid scrolled into view — denominator for badge click-through rate. */
export function trackBadgeGridImpression(placement: string, slugs: string[]) {
  track("series_badge_impression", {
    target: placement,
    metadata: { placement, series_count: slugs.length, series_slugs: slugs.join(",") },
  });
}
